import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ArrowRight, Award, Film, Sparkles, X } from 'lucide-react';
import { AnimatedSection, AnimatedText, CountUp, StaggerChildren, StaggerItem } from '@/components/AnimatedComponents';
import VideoPlayer from '@/components/VideoPlayer';
import { useState } from 'react';

const featuredProjects = [
  {
    id: 1,
    title: 'Broadcast & Online Content',
    category: 'Commercial',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/vykupgss_Terry_homepage_photo_03_800x600.jpg',
    link: '/commercial-work',
  },
  {
    id: 2,
    title: 'Washed Away',
    category: 'Documentary',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/tetfj4m6_Terry_FFH_photo_01.jpg',
    link: '/films-for-humanity',
  },
  {
    id: 3,
    title: '15+ Years Filming Events',
    category: 'Event Coverage',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/h7dqlua1_Terry_commercial_events_01.jpg',
    link: '/blog/event-filming',
  },
];

const stats = [
  { number: 17, suffix: '+', label: 'Years Experience' },
  { number: 200, suffix: '+', label: 'Projects Completed' },
  { number: 15, suffix: '', label: 'Film Festival Wins' },
  { number: 50, suffix: '+', label: 'Brand Partnerships' },
];

const clients = [
  'NRL', 'LG', 'Harvey Norman', 'Husqvarna', 'Gardena', 
  'Canon', 'Bosch', 'Bendigo Bank', 'Somfy', 'Qudos Bank'
];

export default function Home() {
  const [showVideoModal, setShowVideoModal] = useState(false);

  return (
    <main data-testid="home-page">
      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
                data-testid="close-video-modal"
              >
                <X size={32} />
              </button>
              <div className="aspect-video">
                <iframe
                  src="https://player.vimeo.com/video/409449474?autoplay=1&title=0&byline=0&portrait=0"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Showreel 2025"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Hero Section */}
      <section className="hero-section grain-overlay" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/npmy625n_Terry_homepage_photo_01.jpg)'
          }}
        />
        <div className="hero-overlay" />

        {/* Hero Content */}
        <div className="hero-content">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="caption mb-6"
          >
            Australian Filmmaker & Cinematographer
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-[10vw] font-bold leading-none tracking-tighter mb-8"
          >
            TERRY<br />
            <span className="text-gradient">WEBSTER</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-[#A1A1AA] text-lg md:text-xl max-w-xl mx-auto mb-10"
          >
            17 years crafting stories that matter. Commercial films, documentaries, 
            and event coverage across 45 countries.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button 
              onClick={() => setShowVideoModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="watch-showreel-btn"
            >
              <Play size={18} />
              Watch Showreel
            </button>
            <Link 
              to="/contact" 
              className="btn-secondary flex items-center gap-2"
              data-testid="hire-me-btn"
            >
              Hire Me
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#007AFF] rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Showreel Section */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="showreel-section">
        <div className="container-wide">
          <AnimatedSection>
            <p className="caption mb-4">Featured Work</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-12">
              Showreel <span className="text-[#007AFF]">2025</span>
            </h2>
          </AnimatedSection>
          
          <AnimatedSection delay={0.2}>
            <VideoPlayer 
              vimeoId="409449474" 
              posterImage="https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/hif70de0_Terry_homepage_photo_03.jpg"
              title="Showreel 2025"
              className="rounded-sm"
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 md:py-32 bg-[#0C0C0C]" data-testid="stats-section">
        <div className="container-wide">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <AnimatedSection key={stat.label} delay={index * 0.1} className="text-center">
                <div className="stat-number">
                  <CountUp end={stat.number} suffix={stat.suffix} />
                </div>
                <p className="text-[#A1A1AA] mt-2">{stat.label}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="featured-projects-section">
        <div className="container-wide">
          <AnimatedSection className="flex flex-col md:flex-row md:items-end justify-between mb-16">
            <div>
              <p className="caption mb-4">Selected Work</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter">
                Featured<br />Projects
              </h2>
            </div>
            <Link 
              to="/commercial-work" 
              className="btn-secondary mt-6 md:mt-0 inline-flex items-center gap-2 self-start"
              data-testid="view-all-projects-link"
            >
              View All Work
              <ArrowRight size={18} />
            </Link>
          </AnimatedSection>

          <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProjects.map((project) => (
              <StaggerItem key={project.id}>
                <Link 
                  to={project.link}
                  className="project-card block group"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="image-hover-scale aspect-[4/3]">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-[#007AFF] text-sm font-medium mb-2">{project.category}</p>
                    <h3 className="text-xl font-bold group-hover:text-[#007AFF] transition-colors">
                      {project.title}
                    </h3>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 bg-[#0C0C0C]" data-testid="services-section">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-16">
            <p className="caption mb-4">What I Do</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter">
              Services
            </h2>
          </AnimatedSection>

          <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StaggerItem>
              <div className="p-8 border border-[#27272A] hover:border-[#007AFF] transition-colors">
                <Film className="w-12 h-12 text-[#007AFF] mb-6" />
                <h3 className="text-2xl font-bold mb-4">Commercial Production</h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  High-end brand campaigns and commercial content that captivates 
                  audiences and drives results.
                </p>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="p-8 border border-[#27272A] hover:border-[#007AFF] transition-colors">
                <Award className="w-12 h-12 text-[#007AFF] mb-6" />
                <h3 className="text-2xl font-bold mb-4">Documentary Filmmaking</h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Award-winning documentaries that tell powerful stories and 
                  create lasting impact for humanitarian causes.
                </p>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="p-8 border border-[#27272A] hover:border-[#007AFF] transition-colors">
                <Sparkles className="w-12 h-12 text-[#007AFF] mb-6" />
                <h3 className="text-2xl font-bold mb-4">AI-Powered Production</h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Cutting-edge AI integration to enhance creative workflows 
                  and deliver innovative visual content.
                </p>
              </div>
            </StaggerItem>
          </StaggerChildren>
        </div>
      </section>

      {/* Client Marquee */}
      <section className="py-12 bg-[#050505] border-y border-[#27272A] overflow-hidden" data-testid="clients-section">
        <div className="flex animate-marquee items-center">
          {[...clients, ...clients].map((client, index) => (
            <span 
              key={`client-${client.replace(/\s+/g, '-').toLowerCase()}-${index}`} 
              className="text-[#71717A] text-lg md:text-xl font-semibold uppercase tracking-widest whitespace-nowrap mx-8 md:mx-12 hover:text-white transition-colors"
            >
              {client}
            </span>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0C0C0C]" data-testid="cta-section">
        <div className="container-wide text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter mb-8">
              Let&apos;s Create<br />
              <span className="text-[#007AFF]">Something Amazing</span>
            </h2>
            <p className="text-[#A1A1AA] text-lg md:text-xl max-w-2xl mx-auto mb-10">
              Ready to bring your vision to life? I&apos;m available for commercial projects, 
              documentary collaborations, and innovative productions.
            </p>
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="get-in-touch-btn"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
}
